import React from 'react';
import { RiErrorWarningLine } from 'react-icons/ri';
import ValidatedInput from '../ui/ValidatedInput';
import type { ProductForm } from '../../services/productService';

/**
 * ช่องกรอกของฟอร์มสินค้า — ย้ายจาก renderProductForm ในโมดัลของ ProductPage (28 ต.ค. 2026)
 * ช่องเท่าเดิมทุกช่อง · ทุกช่องที่ส่งไป backend มีที่แสดง formErrors.<ชื่อช่อง> (BUG-S2111-002)
 * setState แบบ functional (ไม่อ่าน formData จาก closure เก่า)
 */
export interface ProductFormFieldsProps {
  formData: ProductForm;
  setFormData: React.Dispatch<React.SetStateAction<ProductForm>>;
  formErrors: Record<string, string>;
  setFormErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  codeError?: string;
}

const ProductFormFields: React.FC<ProductFormFieldsProps> = ({
  formData, setFormData, formErrors, setFormErrors, codeError,
}) => {
  const clearError = (name: string) => {
    setFormErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    // แปลงค่าสำหรับ number inputs (เหมือนโมดัลเดิม: ว่าง = 0)
    const processedValue: any = type === 'number' ? (value === '' ? 0 : parseFloat(value) || 0) : value;
    setFormData((prev) => ({ ...prev, [name]: processedValue }));
    clearError(name);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  return (
    <div className="space-y-4">
      {/* รหัสสินค้า - readonly ในทุกโหมด (สร้างอัตโนมัติ) */}
      <div>
        <ValidatedInput
          label="รหัสสินค้า"
          name="code"
          value={formData.code}
          onChange={handleInputChange}
          required
          disabled={true}
          placeholder="รหัสจะสร้างอัตโนมัติ"
          error={formErrors.code}
        />
        {codeError && (
          <p className="mt-1 text-sm text-danger-600 flex items-center">
            <RiErrorWarningLine className="w-4 h-4 mr-1 flex-shrink-0" />
            {codeError}
          </p>
        )}
      </div>

      {/* ชื่อสินค้า */}
      <ValidatedInput
        label="ชื่อสินค้า"
        name="name"
        value={formData.name}
        onChange={handleInputChange}
        required
        placeholder="ใส่ชื่อสินค้า"
        error={formErrors.name}
      />

      {/* รายละเอียด */}
      <div>
        <label htmlFor="product-description" className="block text-sm font-medium text-secondary-700">รายละเอียด</label>
        <textarea
          id="product-description"
          aria-label="รายละเอียด"
          name="description"
          value={formData.description || ''}
          onChange={handleInputChange}
          rows={3}
          className={`mt-1 input w-full ${formErrors.description ? 'border-danger-500' : ''}`}
          aria-invalid={Boolean(formErrors.description)}
          aria-describedby={formErrors.description ? 'description-error' : undefined}
        />
        {formErrors.description && (
          <p id="description-error" className="mt-1 text-sm text-danger-600">{formErrors.description}</p>
        )}
      </div>

      {/* ค่าเช่ารายเดือน และ เงินมัดจำ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="min-w-0">
          <ValidatedInput
            label="ค่าเช่ารายเดือน (บาท) — ไม่บังคับ"
            type="number"
            name="monthlyRentalRate"
            value={formData.monthlyRentalRate || 0}
            onChange={handleInputChange}
            min="0"
            step="0.01"
            placeholder="0.00"
            error={formErrors.monthlyRentalRate}
          />
        </div>
        <div className="min-w-0">
          <label htmlFor="product-depositAmount" className="block text-sm font-medium text-secondary-700">เงินมัดจำ (บาท)</label>
          <input
            id="product-depositAmount"
            type="number"
            aria-label="เงินมัดจำ (บาท)"
            name="depositAmount"
            value={formData.depositAmount || 0}
            onChange={handleInputChange}
            min="0"
            step="0.01"
            className={`mt-1 input w-full ${formErrors.depositAmount ? 'border-danger-500' : ''}`}
            aria-invalid={Boolean(formErrors.depositAmount)}
            aria-describedby={formErrors.depositAmount ? 'depositAmount-error' : undefined}
          />
          {formErrors.depositAmount && (
            <p id="depositAmount-error" className="mt-1 text-sm text-danger-600">{formErrors.depositAmount}</p>
          )}
        </div>
      </div>

      {/* จำนวนวันเช่าขั้นต่ำ และ หน่วย */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="min-w-0">
          <label htmlFor="product-minimumRentalDays" className="block text-sm font-medium text-secondary-700">จำนวนวันเช่าขั้นต่ำ</label>
          <input
            id="product-minimumRentalDays"
            type="number"
            aria-label="จำนวนวันเช่าขั้นต่ำ"
            name="minimumRentalDays"
            value={formData.minimumRentalDays || 30}
            onChange={handleInputChange}
            min="1"
            className={`mt-1 input w-full ${formErrors.minimumRentalDays ? 'border-danger-500' : ''}`}
            aria-invalid={Boolean(formErrors.minimumRentalDays)}
            aria-describedby={formErrors.minimumRentalDays ? 'minimumRentalDays-error' : undefined}
          />
          {formErrors.minimumRentalDays && (
            <p id="minimumRentalDays-error" className="mt-1 text-sm text-danger-600">{formErrors.minimumRentalDays}</p>
          )}
        </div>
        <div className="min-w-0">
          <label htmlFor="product-unit" className="block text-sm font-medium text-secondary-700">หน่วย</label>
          <input
            id="product-unit"
            aria-label="หน่วย"
            type="text"
            name="unit"
            value={formData.unit || ''}
            onChange={handleInputChange}
            className={`mt-1 input w-full ${formErrors.unit ? 'border-danger-500' : ''}`}
            placeholder="เช่น เครื่อง, ชิ้น, ชุด"
            aria-invalid={Boolean(formErrors.unit)}
            aria-describedby={formErrors.unit ? 'unit-error' : undefined}
          />
          {/* KD3 S2111 (BUG-S2111-002): 422 หน่วยยาวเกินเคยเงียบ — map field error ได้แต่ไม่มีที่แสดง */}
          {formErrors.unit && (
            <p id="unit-error" className="mt-1 text-sm text-danger-600">{formErrors.unit}</p>
          )}
        </div>
      </div>

      {/* สถานะ */}
      <div className="flex items-center space-x-3">
        <input
          type="checkbox"
          id="isActive"
          name="isActive"
          checked={formData.isActive !== undefined ? formData.isActive : true}
          onChange={handleCheckboxChange}
          className="h-4 w-4 text-primary-600 rounded border-secondary-300 focus:ring-primary-500"
        />
        <label htmlFor="isActive" className="text-sm font-medium text-secondary-700">
          เปิดใช้งาน
        </label>
      </div>
    </div>
  );
};

export default ProductFormFields;
